/**
 * Control geometry helpers — single source of truth for the SIZE ladder
 * of pill-shaped controls (Chip, Badge, ChipMUI, любые будущие пилюли).
 *
 * Вертикаль токенов (честная, в рантайме):
 *   spacing.* / control.height.*  (базовый слой, tokens/semantic/*.json)
 *        ▲
 *   PILL_PADDING_X / PILL_GAP     (этот файл — маппинг «ступень → база»)
 *        ▲
 *   pillGeometry(size)            (компоненты читают отсюда)
 *
 * Значения НЕ хардкодятся: padX/gap читают spacing через v(), height —
 * control.height. Единственное, что живёт здесь, — само дизайн-решение
 * «какая ступень spacing соответствует какому размеру пилюли». Меняешь
 * spacing.json — пилюли следуют; меняешь маппинг тут — расходится на все
 * пилюли сразу, без правки компонентов. Так лечится дрейф GEO-таблиц,
 * из-за которого Chip и Badge разъехались по паддингам.
 *
 * Почему не токен-группа в sizing.json: в этом репо `{ref}` в json не
 * резолвятся в рантайме (см. state.ts — ручное зеркало). Держать ladder
 * в json потребовало бы дублирующего TS-зеркала = дрейф. Один источник
 * здесь честнее. Если позже понадобится Figma/token-story видимость —
 * заводим рантайм-резолвер `{ref}` и промотим сюда, retiring state.ts.
 *
 * @format
 */

import spacingTokens from 'ui/tokens/semantic/spacing.json';
import sizingTokens from 'ui/tokens/semantic/sizing.json';
import {v} from 'ui/tokens/util';

import type {TypographyRole} from './typography';

// Базовый слой → число (px). Читаем spacing/height из json, не хардкодим.
// `_comment` — документирующий ключ внутри токен-файла, не токен; без
// Exclude он попадает в union и ломает типизацию v().
type SpacingKey = Exclude<keyof typeof spacingTokens.spacing, '_comment'>;
type HeightKey = keyof typeof sizingTokens.control.height;

const space = (k: SpacingKey): number => parseInt(v(spacingTokens.spacing[k]));
const height = (k: HeightKey): number => parseInt(v(sizingTokens.control.height[k]));

export const CONTROL_SIZES = ['tiny', 'xs', 's', 'sm', 'm', 'l'] as const;
export type ControlSize = (typeof CONTROL_SIZES)[number];

// ── Дизайн-решение: ступень пилюли → базовый токен ──────────────────────
// padX ≈ 0.4·height, снят к существующим ступеням spacing (без новых чисел).
const PILL_PADDING_X: Record<ControlSize, number> = {
  tiny: space('xs-plus'), // 6
  xs: space('s'), // 8
  s: space('s-plus'), // 12
  sm: space('m'), // 16
  m: space('m-plus'), // 20
  l: space('l-plus'), // 28
};

// Сжатый боковой паддинг — та же лестница, на одну ступень ниже padX.
// Нужен со стороны слота, который сам несёт визуальную массу и «доносит»
// отступ собой (крестик удаления, счётчик). Раньше это место занимал
// `Math.round(padX / 2)` — число, выдуманное на месте; теперь ступень.
const PILL_PADDING_X_COMPACT: Record<ControlSize, number> = {
  tiny: space('xxs'), // 2
  xs: space('xs'), // 4
  s: space('xs-plus'), // 6
  sm: space('s'), // 8
  m: space('s-plus'), // 12
  l: space('m'), // 16
};

const PILL_GAP: Record<ControlSize, number> = {
  tiny: space('xxs'), // 2
  xs: space('xs'), // 4
  s: space('xs-plus'), // 6
  sm: space('s'), // 8
  m: space('s'), // 8
  l: space('s-plus'), // 12
};

// Типографическая роль на ступень — ссылка на типо-токены, не магия.
const PILL_LABEL_ROLE: Record<ControlSize, TypographyRole> = {
  tiny: 'label.s',
  xs: 'label.s',
  s: 'label.m',
  sm: 'label.m',
  m: 'label.l',
  l: 'label.l',
};

export interface PillGeometry {
  height: number;
  padX: number;
  /** Боковой паддинг со стороны слота с собственной массой (крестик и т.п.). */
  padXCompact: number;
  gap: number;
  labelRole: TypographyRole;
}

/**
 * Геометрия пилюльного контрола на заданной ступени размерной шкалы.
 * height/padX/gap — из токенов; radius пилюли компонент считает как
 * height/2; размер ведущей иконки и крестика — из labelRole через
 * iconPxForRole (уже токен), поэтому отдельных icon/remove полей нет.
 */
export function pillGeometry(size: ControlSize): PillGeometry {
  return {
    height: height(size),
    padX: PILL_PADDING_X[size],
    padXCompact: PILL_PADDING_X_COMPACT[size],
    gap: PILL_GAP[size],
    labelRole: PILL_LABEL_ROLE[size],
  };
}
